import {
  FSM_Licence,
  FlowsterAIStudioLicence,
  FlowsterStudioReset,
  LicenceTypes,
  productTypes,
} from 'types';
import { FlowsterStudioLicenceModel } from 'types/FlowsterStudioLicenceType';

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isFSM(payload: LicenceTypes): payload is FSM_Licence {
  return (
    isObject(payload) &&
    'Maintenance' in payload &&
    'Support' in payload &&
    'Subscription' in payload
  );
}

export function isPasswordReset(payload: LicenceTypes): payload is FlowsterStudioReset {
  if (!isObject(payload) || !('Support' in payload)) {
    return false;
  }

  const support = payload.Support;
  return isObject(support) && 'Available' in support;
}

export function isFlowsterStudio(payload: LicenceTypes): payload is FlowsterStudioLicenceModel {
  if (!isObject(payload) || !('Subscription' in payload) || !('Support' in payload)) {
    return false;
  }

  const subscription = payload.Subscription;
  const support = payload.Support;

  return (
    isObject(subscription) &&
    'Type' in subscription &&
    isObject(support) &&
    !('Available' in support)
  );
}

export function isFlowsterAIStudio(payload: LicenceTypes): payload is FlowsterAIStudioLicence {
  return isObject(payload) && 'iss' in payload && 'planName' in payload && 'features' in payload;
}

export function isJwtToken(value: string | null | undefined): boolean {
  if (!value) return false;
  const parts = value.trim().split('.');
  if (parts.length !== 3 || parts.some((part) => !/^[A-Za-z0-9_-]+$/.test(part))) {
    return false;
  }
  try {
    const base64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const header = JSON.parse(atob(padded));
    return isObject(header) && 'alg' in header;
  } catch {
    return false;
  }
}

export function returnTypedParse(
  parsedLicence: LicenceTypes,
  product: 'FSM'
): FSM_Licence;
export function returnTypedParse(
  parsedLicence: LicenceTypes,
  product: 'Password Reset Portal'
): FlowsterStudioReset;
export function returnTypedParse(
  parsedLicence: LicenceTypes,
  product: 'Flowster Studio'
): FlowsterStudioLicenceModel;
export function returnTypedParse(
  parsedLicence: LicenceTypes,
  product: 'Flowster AI Studio'
): FlowsterAIStudioLicence;
export function returnTypedParse(
  parsedLicence: LicenceTypes,
  product: productTypes
): LicenceTypes {
  switch (product) {
    case 'FSM': {
      if (isFSM(parsedLicence)) {
        return parsedLicence;
      }
      break;
    }
    case 'Password Reset Portal': {
      if (isPasswordReset(parsedLicence)) {
        return parsedLicence;
      }
      break;
    }
    case 'Flowster Studio': {
      if (isFlowsterStudio(parsedLicence)) {
        return parsedLicence;
      }
      break;
    }
    case 'Flowster AI Studio': {
      if (isFlowsterAIStudio(parsedLicence)) {
        return parsedLicence;
      }
      break;
    }
  }

  throw new Error(`Parsed licence does not match product '${product}'.`);
}
